import { createContext, useContext } from 'react'
import type { Conversation, ConversationGroup } from './useInboxConversations'

/**
 * Alt appen vet om «ulest», ett sted.
 *
 * ## Hvorfor dette finnes
 *
 * David, 15.08.2026: «Sørge for at meldinger og notifications dukker opp riktig
 * med nummer og fjernes når noen har sett dem.» Den første halvdelen virket. Den
 * andre kunne ikke virke, og grunnen var strukturell, ikke en glipp:
 *
 *   - `useInboxConversations` ble kalt TRE ganger — sidemenyen, bunnmenyen og
 *     `/messages`. Tre uavhengige tilstander. `markRead` nullstilte den ene, og
 *     de to menyene beholdt tallet sitt.
 *   - «sist lest» lå i en ref i minnet. Ved neste last var den tom, og hver
 *     melding fra en annen ble talt som ulest på nytt.
 *   - `useUnreadCount` ble kalt to ganger, og de to kallene delte én
 *     sanntidskanal (se `lib/realtime.ts`) — så den ene kunne melde den andre av.
 *
 * Én kilde løser alle tre samtidig: én henting, ett abonnement, én tilstand å
 * nullstille. Tallet i sidemenyen, tallet i bunnmenyen og lista på `/messages`
 * kan ikke lenger være uenige, fordi det bare finnes ett tall.
 *
 * Selve lesekvitteringen ligger i databasen (`chat_reads` for samtaler,
 * `notifications.read` for varsler), ikke her — kontekstet er visningen av en
 * sannhet som overlever både en omlasting og en ny maskin.
 */
export interface UnreadState {
  /** Uleste varsler for den innloggede — talt i databasen, ikke i den lastede lista. */
  notifications: number
  conversations: Conversation[]
  groups: ConversationGroup[]
  /**
   * Summen av uleste meldinger — det menyene tegner.
   *
   * Talt over samtalene som FAKTISK STÅR i innboksen (`groups`), ikke over alle
   * `conversations`. Et merke er en oppfordring til å trykke et sted; et tall
   * for en samtale som er filtrert bort fra lista er en oppfordring uten mål,
   * og det er verre enn ikke noe merke. Se `unreadTotal` i
   * `useInboxConversations`.
   */
  messages: number
  conversationsLoading: boolean
  conversationsError: string | null
  /** `chatKey` er `<event_id>` eller `sub:<sub_event_id>` — se `conversationKey`. */
  markConversationRead: (chatKey: string) => void | Promise<void>
  refetchConversations: () => Promise<void>
}

export const UnreadContext = createContext<UnreadState | null>(null)

/**
 * Samme tilstand, men `null` utenfor provideren i stedet for et kast.
 *
 * Finnes for ÉN grunn, og den er målt: `ChatPane` er den ene komponenten som
 * tegner en samtale, og den rendres to steder — `/messages` (inne i
 * `AppLayout`, altså inne i provideren) og kunngjørings-/chatfanen på
 * deltakersiden, som på ruta `/event/public/:invitationToken` står HELT uten
 * skall (`routes/index.tsx`: den ligger utenfor både `AppLayout` og
 * `PublicLayout`). En gjest med en lenke fra en SMS har ingen innboks å
 * nullstille, og skal ikke krasje av å lese en chat.
 *
 * Bruk `useUnread()` overalt ellers: en meny som viser «0» fordi den står
 * utenfor provideren er nøyaktig den forvekslingen kastet finnes for.
 */
export function useUnreadOptional(): UnreadState | null {
  return useContext(UnreadContext)
}

export function useUnread(): UnreadState {
  const ctx = useContext(UnreadContext)
  if (!ctx) {
    // Bevisst en kastet feil og ikke en stille null-tilstand: en meny som viser
    // «0 uleste» fordi den står utenfor provideren ser nøyaktig ut som en meny
    // uten uleste, og det er den forvekslingen hele denne fila finnes for.
    //
    // Meldinga er på engelsk fordi den aldri når en bruker — den treffer den som
    // monterte komponenten på feil sted, i konsollet.
    throw new Error('useUnread() must be rendered inside <UnreadProvider> — see layouts/AppLayout.tsx')
  }
  return ctx
}
